import type { WikiItem, WikiVehiclePort } from "~~/types/gamedata";

/**
 * Sammelt rekursiv alle Item-UUIDs eines Port-Baums (inkl. verschachtelter
 * Vehicle-Ports, z.B. Raketen unter einem Waffenhalter, Jump-Drive unter
 * einem Quantenantrieb).
 */
function collectPortUuids(list: WikiVehiclePort[]): string[] {
  const uuids: string[] = [];
  for (const port of list) {
    if (port.equipped_item?.uuid) uuids.push(port.equipped_item.uuid);
    if (port.ports?.length) uuids.push(...collectPortUuids(port.ports));
  }
  return uuids;
}

/**
 * Löst die Item-Volldaten aller Ports (inkl. verschachtelter Vehicle- und
 * Katalog-Ports) auf — Ports kennen nur die Namen der verbauten Teile, die
 * Kennzahlen (DPS, Schild-HP, Kühlrate, ...) für den Komponentenbaum stecken
 * erst in den Item-Volldaten. Gemeinsame Grundlage für die Schiffs-Detailseite
 * und den Schiffsvergleich.
 *
 * Jeder `fetchItemsByUuid()`-Aufruf einzeln in `nuxtApp.runWithContext()`
 * gewrappt: dazwischen liegt ein `await`, nach dem ein reaktiv (z.B. über
 * einen `watch`) erneut angestoßener Aufruf sonst den Nuxt-Kontext verliert
 * (`useState`/`useNuxtApp` in `fetchItemsByUuid` schlagen dann mit
 * NUXT_E1001 fehl) — live geprüft in `ships/[id].vue`, vor der Extraktion
 * hierher.
 */
export async function resolvePortItems(portList: WikiVehiclePort[]): Promise<Map<string, WikiItem>> {
  const nuxtApp = useNuxtApp();

  // Die Wiki-API erlaubt nur 60 Requests pro Minute. Eingebettete Portdaten
  // reichen bereits für die Darstellung; maximal 60 Item-Details pro Schiff
  // dienen ausschließlich der zusätzlichen Anreicherung.
  const itemBudget = 60;
  const topLevelUuids = [...new Set(collectPortUuids(portList))].slice(0, itemBudget);
  const topLevelItems = await nuxtApp.runWithContext(() => fetchItemsByUuid(topLevelUuids));
  // Manche Items verschachteln zusätzlich auf Katalog-Ebene (z.B. Frachtraster
  // in einem Cargo-Modul, `item.ports`) — deren Uuids sind erst nach dem ersten
  // Fetch bekannt, daher ein zweiter Nachlade-Schritt.
  const nestedUuids = [...new Set([...topLevelItems.values()]
    .flatMap((item) => item.ports ?? [])
    .map((port) => port.equipped_item?.uuid)
    .filter((uuid): uuid is string => !!uuid))]
    .slice(0, Math.max(0, itemBudget - topLevelUuids.length));
  const nestedItems = await nuxtApp.runWithContext(() => fetchItemsByUuid(nestedUuids));
  return new Map([...topLevelItems, ...nestedItems]);
}
